import { useEffect, useMemo } from 'react';

import { PreviewHeader } from '@/features/workflows/components/simple/preview-screens';
import { cn } from '@/lib/utils';

import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { formatResultDateTime } from '../../sessionDateTime';
import { formatGender } from '../../sessionFieldFormat';
import { extractError } from '../pan/pan.utils';

const STATUS_STYLE: Record<string, string> = {
  Passed: 'bg-[#DCFCE7] text-[#166534]',
  Review: 'bg-[#FEF9C3] text-[#854D0E]',
  Failed: 'bg-[#FEE2E2] text-[#991B1B]',
  Pending: 'bg-[#F3F4F6] text-[#374151]',
};

// Read-only MSME (Udyam) step detail for the view / review screens. Shows the
// stored, normalised outcome — no re-run form.
export function MsmeDetail({
  step,
  formId,
  onPendingChange,
  onReadyChange,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
  formId: string;
  onPendingChange: (pending: boolean) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  useEffect(() => {
    onPendingChange(false);
    onReadyChange(false);
  }, [onPendingChange, onReadyChange]);

  const error = extractError(step.resultJson);
  const claims = useMemo(() => extractClaims(step.resultJson), [step.resultJson]);

  return (
    <form id={formId} onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-4">
      <PreviewHeader title="MSME Verification (Udyam)" subtitle="Enterprise profile & financials" />

      <div className="flex items-center justify-between rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
        <span className="text-sm text-[#6B7280]">Status</span>
        <span
          className={cn(
            'rounded-full px-2.5 py-0.5 text-xs font-semibold',
            STATUS_STYLE[step.status] ?? STATUS_STYLE.Pending,
          )}
        >
          {step.status}
        </span>
      </div>

      {claims.length > 0 && (
        <div className="flex flex-col divide-y divide-[#F3F4F6] rounded-lg border border-[#E5E7EB]">
          {claims.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <span className="text-sm text-[#6B7280]">{label}</span>
              <span className="text-right text-sm font-medium text-[#111827]">{value}</span>
            </div>
          ))}
        </div>
      )}

      {error && step.status === 'Failed' && <p className="text-xs text-[#BF3636]">{error}</p>}
    </form>
  );
}

// Canonical MSME Return-Data fields, in display order. The stored result is the
// NORMALISED, filtered object, so we render exactly the keys left enabled.
const MSME_FIELD_LABELS: Array<{ key: string; label: string }> = [
  { key: 'udyam_registration_no', label: 'Udyam Registration Number' },
  { key: 'date_of_registration', label: 'Date of Registration' },
  { key: 'type_of_enterprise', label: 'Type of Enterprise' },
  { key: 'enterprise_type_history', label: 'Enterprise Type History' },
  { key: 'industry_classification', label: 'Industry Classification' },
  { key: 'interested_in_treds', label: 'Interested in TReDS' },
  { key: 'interested_in_gem', label: 'Interested in GeM' },
  { key: 'business_name', label: 'Business Name' },
  { key: 'major_activity', label: 'Major Activity' },
  { key: 'organization_type', label: 'Organization Type' },
  { key: 'owner_name', label: 'Owner Name' },
  { key: 'pan', label: 'PAN' },
  { key: 'gender', label: 'Gender' },
  { key: 'social_category', label: 'Social Category' },
  { key: 'specially_abled', label: 'Specially Abled' },
  { key: 'date_of_incorporation', label: 'Date of Incorporation' },
  { key: 'date_of_commencement', label: 'Date of Commencement' },
  { key: 'previous_itr_status', label: 'Previous ITR Status' },
  { key: 'itr_type', label: 'ITR Type' },
  { key: 'gstn_status', label: 'GSTIN Status' },
  { key: 'mobile', label: 'Mobile Number' },
  { key: 'email_id', label: 'Email Address' },
  { key: 'bank_name', label: 'Bank Name' },
  { key: 'ifsc_code', label: 'IFSC Code' },
  { key: 'bank_account_no', label: 'Bank Account Number' },
  { key: 'employment_male', label: 'Male Employees' },
  { key: 'employment_female', label: 'Female Employees' },
  { key: 'employment_others', label: 'Other Employees' },
  { key: 'employment_total', label: 'Total Employees' },
  { key: 'financial_year', label: 'Financial Year' },
  { key: 'wdv_pme', label: 'WDV of Plant & Machinery' },
  { key: 'exclusion', label: 'Exclusion' },
  { key: 'net_investment_in_pme', label: 'Net Investment in P&M' },
  { key: 'total_turnover', label: 'Total Turnover' },
  { key: 'export_turnover', label: 'Export Turnover' },
  { key: 'net_turnover', label: 'Net Turnover' },
  { key: 'official_address', label: 'Official Address' },
  { key: 'branch_details', label: 'Branch Details' },
  { key: 'pdf_document', label: 'Registration Certificate (PDF)' },
  { key: 'enterprise_type_mismatch_flag', label: 'Enterprise Type Mismatch Flag' },
  { key: 'pan_match_flag', label: 'PAN Match Flag' },
  { key: 'turnover_threshold_flag', label: 'Turnover Threshold Flag' },
  { key: 'duplicate_udyam_flag', label: 'Duplicate Udyam Flag' },
  { key: 'verification_status', label: 'Status' },
  { key: 'verification_attempts', label: 'Verification Attempts' },
  { key: 'verified_at', label: 'Verified At' },
  { key: 'method_used', label: 'Method Used' },
  { key: 'session_id', label: 'Session ID' },
];

// Financial / monetary amounts — grouped with the Indian numbering system.
const MONEY_KEYS = new Set([
  'wdv_pme',
  'exclusion',
  'net_investment_in_pme',
  'total_turnover',
  'export_turnover',
  'net_turnover',
]);

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  // Stored as an object-storage key (base64 offloaded) — don't dump the path.
  if (key === 'pdf_document') return 'Available';
  if (key === 'gender') return formatGender(value);
  if (key === 'verified_at') {
    return formatResultDateTime(value);
  }
  if (MONEY_KEYS.has(key)) {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : `₹${n.toLocaleString('en-IN')}`;
  }
  return String(value);
}

function extractClaims(resultJson: string | null): [string, string][] {
  if (!resultJson) return [];
  let parsed: Record<string, unknown> | null = null;
  try {
    const p = JSON.parse(resultJson);
    parsed = p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
  } catch {
    return [];
  }
  if (!parsed) return [];
  const out: [string, string][] = [];
  for (const { key, label } of MSME_FIELD_LABELS) {
    if (key in parsed && parsed[key] !== null && parsed[key] !== '') {
      out.push([label, formatValue(key, parsed[key])]);
    }
  }
  return out;
}
